import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getUsers, grantCourseAccess } from '../../../services/adminService';
import { useAppContext } from '../../../context/AppContext';
import { matchesSearch } from '../../../utils/searchHelpers';

export default function GrantCourseAccessModal({ courseId, courseTitle, existingPurchases, onClose, onSuccess }) {
  const { setToaster, token } = useAppContext();
  const [search, setSearch] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grantingUserId, setGrantingUserId] = useState(null);
  const [grantedUserIds, setGrantedUserIds] = useState(new Set());

  useEffect(() => {
    getUsers(token, 1, '', 1000)
      .then((data) => setAllUsers(data.users || []))
      .catch((error) => {
        console.error('Error fetching users:', error);
        setToaster({ message: 'Failed to load users', type: 'error', show: true });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getUserDisplayName = (user) => {
    const composed = `${user.given_name || ''} ${user.family_name || ''}`.trim();
    if (composed) return composed;
    return user.name || user.nickname || user.username || user.email || user.user_id || '';
  };

  const alreadyHasAccessIds = new Set(
    (existingPurchases || []).map((p) => p.userId?.user_id).filter(Boolean)
  );

  const filteredUsers = allUsers.filter((user) => {
    const displayName = getUserDisplayName(user);
    return matchesSearch(search, [
      displayName,
      user.given_name,
      user.family_name,
      user.name,
      user.nickname,
      user.username,
      user.email,
      user.user_id,
    ]);
  });

  const sortedUsers = filteredUsers.slice().sort((a, b) =>
    getUserDisplayName(a).toLowerCase().localeCompare(getUserDisplayName(b).toLowerCase())
  );

  const handleGrant = async (user) => {
    setGrantingUserId(user.user_id);
    try {
      await grantCourseAccess(token, user.user_id, courseId);
      setGrantedUserIds((prev) => new Set(prev).add(user.user_id));
      setToaster({ message: `Access granted to ${user.email || getUserDisplayName(user)}`, type: 'success', show: true });
      onSuccess?.();
    } catch (error) {
      setToaster({ message: error.message || 'Failed to grant access', type: 'error', show: true });
    } finally {
      setGrantingUserId(null);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-[70]"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold">Grant Course Access</h3>
            {courseTitle && <p className="text-xs text-gray-500">{courseTitle}</p>}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl font-bold">
            ✕
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Search User</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full border border-gray-300 rounded p-2 text-sm"
              autoFocus
            />
          </div>

          {loading && <p className="text-center text-sm text-gray-500">Loading users...</p>}

          {!loading && sortedUsers.length > 0 && (
            <div className="max-h-72 overflow-y-auto border border-gray-200 rounded">
              {sortedUsers.map((user) => {
                const hasAccess = alreadyHasAccessIds.has(user.user_id) || grantedUserIds.has(user.user_id);
                const isGrantingThisUser = grantingUserId === user.user_id;
                return (
                  <div
                    key={user._id}
                    className="flex justify-between items-center p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate">{getUserDisplayName(user)}</p>
                      <p className="text-xs text-gray-600 truncate">{user.email}</p>
                    </div>
                    <button
                      onClick={() => handleGrant(user)}
                      disabled={hasAccess || !!grantingUserId}
                      className="px-3 py-1 rounded text-xs disabled:opacity-50 bg-brown text-white hover:bg-pink shrink-0 ml-2"
                    >
                      {hasAccess ? 'Has access' : isGrantingThisUser ? 'Granting...' : 'Grant'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && sortedUsers.length === 0 && (
            <p className="text-center text-sm text-gray-500">No users found</p>
          )}
        </div>

        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm">
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
